import * as React from "react";
import {
  Button,
  Dialog,
  FormField,
  OtpInput,
  PhoneInput,
  useToast,
} from "@chotca/ui";
import { useConfirmPhoneVerify, useStartPhoneVerify } from "@chotca/api-client";

type Props = {
  open: boolean;
  onClose: () => void;
  /** Prefill (e.g. the org contact phone) so the user rarely retypes it. */
  initialPhone?: string | null;
};

// Two-step account phone verification: enter number → receive a Zalo ZNS OTP →
// enter the 6-digit code. Uses the shared PhoneInput / OtpInput so it matches
// every other phone/OTP surface in the app.
export function PhoneVerifyDialog({ open, onClose, initialPhone }: Props) {
  const { toast } = useToast();
  const start = useStartPhoneVerify();
  const confirm = useConfirmPhoneVerify();

  const [step, setStep] = React.useState<"enter" | "code">("enter");
  const [phone, setPhone] = React.useState(initialPhone ?? "");
  const [code, setCode] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);

  // Reset to a clean state each time the dialog opens.
  React.useEffect(() => {
    if (open) {
      setStep("enter");
      setPhone(initialPhone ?? "");
      setCode("");
      setError(null);
    }
  }, [open, initialPhone]);

  const sendCode = async () => {
    setError(null);
    try {
      await start.mutateAsync({ phone });
      setStep("code");
      toast({ title: "Đã gửi mã xác minh qua Zalo", tone: "success" });
    } catch (err) {
      setError(errMsg(err, "Không gửi được mã. Vui lòng thử lại."));
    }
  };

  const submitCode = async (value: string) => {
    setError(null);
    try {
      await confirm.mutateAsync({ phone, code: value });
      toast({ title: "Đã xác minh số điện thoại", tone: "success" });
      onClose();
    } catch (err) {
      setError(errMsg(err, "Mã không đúng hoặc đã hết hạn."));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Xác minh số điện thoại"
      description={
        step === "enter"
          ? "Chúng tôi sẽ gửi mã xác minh 6 số qua Zalo tới số này."
          : `Nhập mã 6 số vừa gửi tới ${formatPhone(phone)}.`
      }
    >
      {step === "enter" ? (
        <div className="space-y-4">
          <FormField label="Số điện thoại" error={error ?? undefined}>
            <PhoneInput value={phone} onChange={setPhone} invalid={!!error} />
          </FormField>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} type="button">
              Huỷ
            </Button>
            <Button
              onClick={() => void sendCode()}
              loading={start.isPending}
              disabled={phone.replace(/\D/g, "").length < 9}
              type="button"
            >
              Gửi mã
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <FormField label="Mã xác minh" error={error ?? undefined}>
            <OtpInput
              value={code}
              onChange={setCode}
              onComplete={(v) => void submitCode(v)}
              invalid={!!error}
              autoFocus
              disabled={confirm.isPending}
            />
          </FormField>
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => void sendCode()}
              loading={start.isPending}
              type="button"
            >
              Gửi lại mã
            </Button>
            <Button
              onClick={() => void submitCode(code)}
              loading={confirm.isPending}
              disabled={code.length < 6}
              type="button"
            >
              Xác nhận
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}

function formatPhone(p: string): string {
  const d = p.replace(/\D/g, "");
  return d.startsWith("84") ? "0" + d.slice(2) : d;
}

function errMsg(err: unknown, fallback: string): string {
  if (err && typeof err === "object" && "message" in err) {
    const m = (err as { message?: unknown }).message;
    if (typeof m === "string" && m.trim()) return m;
  }
  return fallback;
}
